import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import { Maybe } from '../../core';
import { notifyError, notifySuccess } from '../../services/notifications';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';

export class DeleteJobWindowState
  extends StoreNode {

  readonly nodeType = 'DeleteJobWindow';

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'windowListener'>(this, {
      isLoading: observable,
      jobId: observable,
      noun: computed,
      isWidgetMode: computed,
      submit: action,
      windowListener: action,
      open: action,
      close: action,
    });

    this.window.listen(
      this.windowListener);
  }

  readonly window = new WindowState(this.store);

  isLoading: boolean = false;

  jobId: Maybe<string>;
  teamId: Maybe<string>;

  /** Document jobs share this window with videos, so the wording follows the job. */
  get noun(): 'document' | 'video' {
    return this.store.jobManager.maybeGetJob(this.jobId)?.isDocumentJob ? 'document' : 'video';
  }

  get isWidgetMode() {
    return this.store.widgetService.isWidgetMode;
  }

  async submit() {
    this.isLoading = true;

    const [, err] = await this.store.apiService.archiveJob({ id: this.jobId || '' });

    if (err) {
      notifyError(this, 'Could not delete.');
      return this.handleSubmitFinally();
    }

    this.store.bookmarkManager.bookmarkListLookup.clear();
    await this.store.bookmarkManager.apiFetchBookmarkLists();

    runInAction(() => {
      // TODO: remove hard dependencies to other pages
      // also, on a broader level as discussed previously, no component should interact with pages directly
      notifySuccess(this, this.noun === 'document' ? 'Document deleted.' : 'Video deleted.');
      if (this.teamId) {
        if (this.isWidgetMode) {
          this.store.teamLibraryWidget.refreshJobs();
        } else {
          this.store.teamDashboardPage.refreshJobs();
        }
      } else {
        this.store.userDashboardPage.mounted({});
        this.store.trainerDashboardPage.refreshJobs();
      }
      this.handleSubmitFinally();
      this.emit('JobDeleted')
    });
  }

  handleSubmitFinally() {
    runInAction(() => {
      this.isLoading = false;
      this.close();
    })
  }

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        this.close();
        break;
    }
  }

  open({ jobId, teamId }: { jobId: string, teamId?: string }) {
    this.dispatch('Overlays', 'openWindow', { name: 'DeleteJobWindow' });

    this.jobId = jobId;
    this.teamId = teamId;
  }

  close() {
    this.jobId = null;
    this.teamId = null;

    this.emit('close');
    this.dispatch('Overlays', 'closeWindow');
  }
}